import dayjs from 'dayjs'
import minMax from 'dayjs/plugin/minMax'
import Styles from './AddMoreContentModal.module.scss'
import { Modal } from 'almanac-ui/Modal'
import { Heading } from 'blaze-ui/Heading/Heading'
import { Text } from 'blaze-ui/Text/Text'

import { DateTimePicker } from 'blaze-ui/DateTimePicker/DateTimePicker'
import { Button } from 'blaze-ui/Button'
import { ContentType, Schedule } from 'hooks/queries/schedules/useAddToBrandPlanSchedule'
import { TikTokBrand, TwitterBrand, YouTubeBrand } from 'blaze-ui/icons'
import { InstagramBrand } from 'blaze-ui/icons/24'
import { FacebookBrand, MailchimpBrand, WordPressBrand, GoHighLevelBrand, WixBrand } from 'blaze-ui/icons/35'
import { LinkedInBrand } from 'blaze-ui/icons/32'
import { ReviewPostTimesProps } from './Types'
import { useState } from 'react'
import { ExternalIntegrationEnum } from 'api/clients/ExternalIntegrationsClient'
import { BlogPlatformIntegration, BrandPlanTactic } from 'types'
import { useFlipperFlag } from 'hooks/useFlipperFlag'
import { ChannelIcon } from 'components/ChannelIcon/ChannelIcon'
import { ChannelType } from 'components/CampaignCreationModal/Types'
import { useBlogIntegrations } from 'hooks/useBlogIntegrations'
import { useCurrentBrandPlan } from 'hooks/queries/brandPlans/useCurrentBrandPlan'
import { getBlogPlatformBrandingWithOnboardingSelection } from 'screens/Calendar/WeekViewV2/CalendarEventCard/utils'
import CrossPostIcon from 'components/FirstTimeExperienceModal/steps/BrandPlanAwareness/CrossPostIcon'
import { Credits } from 'blaze-ui/icons/20/Credits'

dayjs.extend(minMax)
const has = (tactics: BrandPlanTactic[], t: BrandPlanTactic) => tactics.includes(t)

const iconForContentType = (
  type: ContentType,
  selectedBlogPlatform?: BlogPlatformIntegration,
  tactics: BrandPlanTactic[] = [],
) => {
  switch (type) {
    case 'crosspost_post':
      return (
        <CrossPostIcon
          providers={
            [
              has(tactics, BrandPlanTactic.Instagram) && ExternalIntegrationEnum.Instagram,
              has(tactics, BrandPlanTactic.Facebook) && ExternalIntegrationEnum.Facebook,
              has(tactics, BrandPlanTactic.Twitter) && ExternalIntegrationEnum.X,
              has(tactics, BrandPlanTactic.LinkedIn) && ExternalIntegrationEnum.LinkedIn,
            ].filter(Boolean) as ExternalIntegrationEnum[]
          }
        />
      )
    case 'video_crosspost_post':
      return (
        <CrossPostIcon
          providers={
            [
              has(tactics, BrandPlanTactic.TikTok) && ExternalIntegrationEnum.TikTok,
              has(tactics, BrandPlanTactic.InstagramReel) && ExternalIntegrationEnum.Instagram,
              has(tactics, BrandPlanTactic.YouTube) && ExternalIntegrationEnum.YouTube,
            ].filter(Boolean) as ExternalIntegrationEnum[]
          }
        />
      )
    case 'instagram_post':
    case 'instagram_reel':
    case 'instagram_story':
      return <InstagramBrand size={20} />
    case 'tiktok_post':
      return <TikTokBrand size={20} />
    case 'youtube_short':
      return <YouTubeBrand size={20} />
    case 'twitter_post':
    case 'twitter_thread':
      return <TwitterBrand size={20} />
    case 'facebook_post':
      return <FacebookBrand size={20} />
    case 'linkedin_post':
      return <LinkedInBrand size={20} />
    case 'newsletter':
      return <MailchimpBrand size={20} />
    case 'blog_post':
      switch (selectedBlogPlatform) {
        case ExternalIntegrationEnum.WordPress:
          return <WordPressBrand size={20} />
        case ExternalIntegrationEnum.GoHighLevel:
          return <GoHighLevelBrand size={20} />
        case ExternalIntegrationEnum.Wix:
          return <WixBrand size={20} />
        default:
          return <ChannelIcon size={20} channel={ChannelType.BlogPost} />
      }
    default:
      return null
  }
}

const nameForContentType = (type: ContentType, selectedBlogPlatform?: BlogPlatformIntegration) => {
  switch (type) {
    case 'crosspost_post':
      return 'Cross Post'
    case 'video_crosspost_post':
      return 'Video Cross Post'
    case 'instagram_post':
      return 'Instagram Post'
    case 'instagram_reel':
      return 'Instagram Reel'
    case 'instagram_story':
      return 'Instagram Story'
    case 'tiktok_post':
      return 'TikTok Post'
    case 'youtube_short':
      return 'YouTube Short'
    case 'twitter_post':
      return 'X/Twitter Post'
    case 'twitter_thread':
      return 'X/Twitter Thread'
    case 'facebook_post':
      return 'Facebook Post'
    case 'linkedin_post':
      return 'LinkedIn Post'
    case 'newsletter':
      return 'Newsletter'
    case 'blog_post':
      switch (selectedBlogPlatform) {
        case ExternalIntegrationEnum.WordPress:
          return 'WordPress Blog Post'
        case ExternalIntegrationEnum.GoHighLevel:
          return 'GoHighLevel Blog Post'
        case ExternalIntegrationEnum.Wix:
          return 'Wix Blog Post'
        default:
          return 'Blog Post'
      }
    default:
      return null
  }
}

export const ReviewPostTimes = ({
  onBack,
  onConfirm,
  schedule,
  setSchedule,
  targetSchedule,
  isLoading,
  disableDatePicker = false,
  creditsNeeded,
  needsCredits,
}: ReviewPostTimesProps) => {
  const [openPickerId, setOpenPickerId] = useState<string | null>(null)
  const goHighLevelEnabled = useFlipperFlag('go_high_level_integration')
  const wixEnabled = useFlipperFlag('wix_blog_integration')
  const blogPlatformOnboardingBrandingEnabled = useFlipperFlag('blog_platform_onboarding_branding')
  const tactics = targetSchedule?.tactics || []

  const { connectedBlogIntegration } = useBlogIntegrations()
  const { data: brandPlan } = useCurrentBrandPlan()

  const enhancedSelectedBlogPlatform = getBlogPlatformBrandingWithOnboardingSelection({
    channel: ChannelType.BlogPost,
    externalIntegrations: connectedBlogIntegration ? [connectedBlogIntegration] : [],
    selectedBlogPlatform: brandPlan?.selected_blog_platform,
    featureFlags: {
      goHighLevelEnabled,
      wixEnabled,
      blogPlatformOnboardingBrandingEnabled,
    },
  })
  const targetScheduleStart = dayjs(targetSchedule.start_date)
  const disableDatesBefore = dayjs.max(dayjs(), targetScheduleStart).format('YYYY-MM-DD')
  const disableDatesAfter = targetScheduleStart.add(targetSchedule.length * 7, 'days').format('YYYY-MM-DD')
  const scheduleGroupedByDate = schedule.reduce((acc, item: Schedule) => {
    const date = dayjs(item.post_at).format('YYYY-MM-DD')
    if (!acc[date]) {
      acc[date] = []
    }
    acc[date].push(item)
    return acc
  }, {} as Record<string, Schedule[]>)

  return (
    <>
      <div className={Styles.postTimesHeader}>
        <Modal.BackButton onPress={onBack} />
        <Heading level={3}>Review Post Times</Heading>
      </div>
      <Modal.Content>
        <Text variant="secondary" color="var(--dark-80)">
          We have distributed your posts evenly from the start of the selected period.
        </Text>
        <div className={Styles.schedule}>
          {Object.keys(scheduleGroupedByDate)
            .sort((a, b) => dayjs(a).unix() - dayjs(b).unix())
            .map((date) => {
              const items = scheduleGroupedByDate[date]

              return (
                <div key={date} className={Styles.scheduleDateContainer}>
                  <Heading level={4}>{dayjs(date).format('dddd MMM D')}</Heading>
                  <div className={Styles.scheduleContainer}>
                    {items.map((item) => {
                      const itemKey = `schedule-item-${schedule.indexOf(item)}`
                      const isOpen = openPickerId === itemKey

                      return (
                        <div key={itemKey} className={Styles.scheduleItem}>
                          <div className={Styles.channelName}>
                            {iconForContentType(
                              item.type,
                              (enhancedSelectedBlogPlatform as BlogPlatformIntegration) || undefined,
                              tactics,
                            )}
                            <Text variant="largeList">
                              {nameForContentType(
                                item.type,
                                (enhancedSelectedBlogPlatform as BlogPlatformIntegration) || undefined,
                              )}
                            </Text>
                          </div>
                          <DateTimePicker
                            dateTime={item.post_at}
                            setDateTime={(dateTime) => {
                              if (!dateTime) return
                              const indexToUpdate = schedule.indexOf(item)
                              const updatedSchedule = [...schedule]
                              updatedSchedule[indexToUpdate].post_at = dateTime
                              setSchedule(updatedSchedule)
                            }}
                            isDateDisabled={disableDatePicker}
                            disableDatesBefore={disableDatesBefore}
                            disableDatesAfter={disableDatesAfter}
                            isOpen={isOpen}
                            onOpenChange={(open) => {
                              setOpenPickerId(open ? itemKey : null)
                            }}
                          >
                            <Button withChevron="down" variant="secondary" size="sm">
                              <Text>{dayjs(item.post_at).format('h:mma z')}</Text>
                            </Button>
                          </DateTimePicker>
                        </div>
                      )
                    })}
                  </div>
                </div>
              )
            })}
        </div>
      </Modal.Content>
      <Modal.Footer>
        <Modal.FooterContent slot="left">
          <Modal.FooterButton variant="tertiary" size="lg" onPress={onBack}>
            Back
          </Modal.FooterButton>
        </Modal.FooterContent>
        <Modal.FooterContent slot="right">
          <Modal.FooterButton onPress={onConfirm} size="lg" isDisabled={isLoading}>
            {needsCredits ? (
              <div className={Styles.buttonWithCredits}>
                <span>Add Credits to Generate</span>
                <Credits color="var(--light-100)" />
                <span>{creditsNeeded}</span>
              </div>
            ) : (
              <div className={Styles.buttonWithCredits}>
                <span>Generate Content</span>
                <Credits color="var(--light-100)" />
                <span>{creditsNeeded}</span>
              </div>
            )}
          </Modal.FooterButton>
        </Modal.FooterContent>
      </Modal.Footer>
    </>
  )
}
